import React, { useMemo, useState } from "react";
import { BarChart3, CalendarRange, Columns3, Filter as FilterIcon, Layers, LineChart, Plus, Sigma, Table2, Target, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { DATE_PRESETS, OP_LABEL, valueCount, withoutChip, type Answer, type Catalog, type EntityDef, type FieldDef, type Filter, type LinkCheck, type Op, type Plan } from "@/components/ai/askReport";

const SELECT = "h-9 w-full rounded-md border border-input bg-background px-2 text-sm font-normal text-foreground";

/**
 * The report's conditions as chips (× takes one off) and the tools to change it: add a filter or a has / has-no check,
 * pick columns, break it down, choose what to show, set the dates, compare, targets and the chart. Every change runs the
 * search again without the AI.
 */
export function ReportBar({ plan, answer, catalog, busy, onChange }: { plan: Plan; answer: Answer; catalog: Catalog; busy: boolean; onChange: (next: Plan) => void }) {
  const ent = catalog.entities.find((e) => e.key === plan.entity);
  const [adding, setAdding] = useState<"filter" | "link" | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  if (!ent) return null;
  const chips = answer.chips || [];
  const set = (patch: Partial<Plan>) => onChange({ ...plan, ...patch });

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-1.5" aria-label="Conditions">
        <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium">{ent.label}</span>
        {chips.map((c) => {
          const filterAt = c.key.startsWith("filter:") ? Number(c.key.slice(7)) : null;
          return (
            <span key={c.key} className="flex max-w-full items-center gap-0.5 rounded-full border border-border bg-card py-0.5 pl-2.5 pr-1 text-xs">
              {filterAt !== null ? (
                <button type="button" className="truncate hover:text-primary" title="Change this condition" onClick={() => setEditing(filterAt)} disabled={busy}>
                  {c.label}
                </button>
              ) : (
                <span className="truncate">{c.label}</span>
              )}
              <button type="button" aria-label={`Take off: ${c.label}`} title="Take this off" disabled={busy} onClick={() => onChange(withoutChip(plan, c.key))} className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground">
                <X className="size-3" />
              </button>
            </span>
          );
        })}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="ghost" className="h-7 gap-1 rounded-full px-2.5 text-xs" disabled={busy}>
              <Plus className="size-3.5" /> Filter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => setAdding("filter")}>
              <FilterIcon className="size-3.5" /> On a field…
            </DropdownMenuItem>
            {ent.links.length > 0 && (
              <DropdownMenuItem onSelect={() => setAdding("link")}>
                <Layers className="size-3.5" /> Has / has no…
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <ColumnsMenu ent={ent} plan={plan} busy={busy} onChange={(columns) => set({ columns })} />
        <BreakdownMenu ent={ent} plan={plan} busy={busy} onChange={set} />
        <MeasureMenu ent={ent} plan={plan} busy={busy} onChange={set} />
        <DatesMenu ent={ent} plan={plan} busy={busy} onChange={set} />
        {ent.target && (
          <Button size="sm" variant={plan.with_target ? "secondary" : "outline"} className="h-8 gap-1.5" disabled={busy} onClick={() => set({ with_target: !plan.with_target })} aria-pressed={plan.with_target}>
            <Target className="size-3.5" /> Target
          </Button>
        )}
        {(answer.groups?.length || 0) > 0 && !answer.group2_label && <ChartToggle plan={plan} answer={answer} busy={busy} onChange={(chart) => set({ chart })} />}
      </div>

      {(adding === "filter" || editing !== null) && (
        <FilterDialog
          ent={ent}
          catalog={catalog}
          initial={editing !== null ? plan.filters[editing] : undefined}
          onClose={() => {
            setAdding(null);
            setEditing(null);
          }}
          onSave={(f) => {
            set({ filters: editing !== null ? plan.filters.map((x, i) => (i === editing ? f : x)) : [...plan.filters, f] });
            setAdding(null);
            setEditing(null);
          }}
        />
      )}
      {adding === "link" && (
        <LinkDialog
          ent={ent}
          onClose={() => setAdding(null)}
          onSave={(l) => {
            set({ links: [...plan.links, l] });
            setAdding(null);
          }}
        />
      )}
    </div>
  );
}

function ColumnsMenu({ ent, plan, busy, onChange }: { ent: EntityDef; plan: Plan; busy: boolean; onChange: (cols: string[]) => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={busy}>
          <Columns3 className="size-3.5" /> Columns{plan.columns.length ? ` · ${plan.columns.length}` : ""}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[60vh] w-60 overflow-y-auto">
        <DropdownMenuLabel className="text-xs text-muted-foreground">Show these as well (up to 12)</DropdownMenuLabel>
        {ent.fields.map((f) => {
          const on = plan.columns.includes(f.key);
          return (
            <DropdownMenuCheckboxItem
              key={f.key}
              checked={on}
              disabled={!on && plan.columns.length >= 12}
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={() => onChange(on ? plan.columns.filter((c) => c !== f.key) : [...plan.columns, f.key])}
            >
              {f.label}
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function BreakdownMenu({ ent, plan, busy, onChange }: { ent: EntityDef; plan: Plan; busy: boolean; onChange: (p: Partial<Plan>) => void }) {
  const norm = (k: string) => (["WEEK", "MONTH", "QUARTER", "PRODUCT", "NONE"].includes(k.toUpperCase()) ? k.toUpperCase() : k.toLowerCase());
  const g1 = norm(plan.group_by || "NONE");
  const g2 = norm(plan.group_by2 || "NONE");
  const name = (k: string) => ent.groups.find((g) => g.key === k)?.label;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={busy}>
          <BarChart3 className="size-3.5" /> {g1 !== "NONE" && name(g1) ? `By ${name(g1)!.toLowerCase()}${g2 !== "NONE" && name(g2) ? ` × ${name(g2)!.toLowerCase()}` : ""}` : "Breakdown"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[60vh] w-60 overflow-y-auto">
        <DropdownMenuRadioGroup value={g1} onValueChange={(v) => onChange({ group_by: v, group_by2: v === "NONE" || v === g2 ? "NONE" : plan.group_by2 })}>
          <DropdownMenuRadioItem value="NONE">No breakdown</DropdownMenuRadioItem>
          {ent.groups.map((g) => (
            <DropdownMenuRadioItem key={g.key} value={g.key}>
              By {g.label.toLowerCase()}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {g1 !== "NONE" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>And by…</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-[60vh] overflow-y-auto">
                <DropdownMenuRadioGroup value={g2} onValueChange={(v) => onChange({ group_by2: v })}>
                  <DropdownMenuRadioItem value="NONE">Nothing else</DropdownMenuRadioItem>
                  {ent.groups
                    .filter((g) => g.key !== g1)
                    .map((g) => (
                      <DropdownMenuRadioItem key={g.key} value={g.key}>
                        {g.label}
                      </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MeasureMenu({ ent, plan, busy, onChange }: { ent: EntityDef; plan: Plan; busy: boolean; onChange: (p: Partial<Plan>) => void }) {
  const valueName = (k: string | null) => (k ? ent.values.find((v) => v.key === k)?.label : ent.amount ? "value" : undefined)?.toLowerCase();
  const label =
    plan.measure === "TOTAL" ? `Total ${valueName(plan.value_field) || ""}` : plan.measure === "AVERAGE" ? `Average ${valueName(plan.value_field) || ""}` : plan.measure === "RATE" ? "Share (rate)" : plan.measure === "COUNT" ? "How many" : "List";
  const pick = (measure: Plan["measure"], value_field: string | null = null) => onChange({ measure, value_field, rate_statuses: measure === "RATE" ? plan.rate_statuses : [] });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={busy}>
          <Sigma className="size-3.5" /> {label.trim()}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[60vh] w-60 overflow-y-auto">
        <DropdownMenuItem onSelect={() => pick("LIST")}>List the records</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => pick("COUNT")}>How many</DropdownMenuItem>
        {ent.values.length > 0 && (
          <>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Total of…</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-[60vh] overflow-y-auto">
                {ent.values.map((v) => (
                  <DropdownMenuItem key={v.key} onSelect={() => pick("TOTAL", v.key)}>
                    {v.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>Average of…</DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="max-h-[60vh] overflow-y-auto">
                {ent.values.map((v) => (
                  <DropdownMenuItem key={v.key} onSelect={() => pick("AVERAGE", v.key)}>
                    {v.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </>
        )}
        {ent.statuses.length > 0 && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>Share that are…</DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {ent.statuses.map((s) => {
                const on = plan.measure === "RATE" && plan.rate_statuses.includes(s.value);
                return (
                  <DropdownMenuCheckboxItem
                    key={s.value}
                    checked={on}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={() => {
                      const cur = plan.measure === "RATE" ? plan.rate_statuses : [];
                      const next = on ? cur.filter((x) => x !== s.value) : [...cur, s.value];
                      onChange({ measure: next.length ? "RATE" : "COUNT", rate_statuses: next, value_field: null });
                    }}
                  >
                    {s.label}
                  </DropdownMenuCheckboxItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function DatesMenu({ ent, plan, busy, onChange }: { ent: EntityDef; plan: Plan; busy: boolean; onChange: (p: Partial<Plan>) => void }) {
  const field = plan.date_field !== "NONE" && ent.dates.some((d) => d.value === plan.date_field) ? plan.date_field : ent.dates[0]?.value;
  const preset = plan.date_field !== "NONE" ? plan.date_preset : "NONE";
  const presetName = DATE_PRESETS.find((d) => d.value === preset)?.label || (preset === "CUSTOM" ? "Chosen dates" : preset === "LAST_N_DAYS" ? `Last ${plan.date_days || 7} days` : null);
  if (!field) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" disabled={busy}>
          <CalendarRange className="size-3.5" /> {presetName || "Dates"}
          {plan.compare !== "NONE" ? " · compared" : ""}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[70vh] w-60 overflow-y-auto">
        {ent.dates.length > 1 && (
          <>
            <DropdownMenuLabel className="text-xs text-muted-foreground">Which date</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={field} onValueChange={(v) => onChange({ date_field: v, date_preset: preset === "NONE" ? "THIS_MONTH" : preset })}>
              {ent.dates.map((d) => (
                <DropdownMenuRadioItem key={d.value} value={d.value} onSelect={(e) => e.preventDefault()}>
                  {d.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuRadioGroup
          value={preset}
          onValueChange={(v) => onChange(v === "NONE" ? { date_field: "NONE", date_preset: "NONE", date_from: null, date_to: null, compare: "NONE" } : { date_field: field, date_preset: v, date_days: v === "LAST_N_DAYS" ? 30 : null, date_from: null, date_to: null })}
        >
          <DropdownMenuRadioItem value="NONE">Any time</DropdownMenuRadioItem>
          {DATE_PRESETS.map((d) => (
            <DropdownMenuRadioItem key={d.value} value={d.value}>
              {d.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        {preset !== "NONE" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">Compare with</DropdownMenuLabel>
            <DropdownMenuRadioGroup value={plan.compare} onValueChange={(v) => onChange({ compare: v as Plan["compare"] })}>
              <DropdownMenuRadioItem value="NONE">Nothing</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="PREVIOUS_PERIOD">The period before</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="LAST_YEAR">The same period last year</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Bars or a line for a one-way breakdown, or just the table; a pie or funnel asked for is drawn as ranked bars */
function ChartToggle({ plan, answer, busy, onChange }: { plan: Plan; answer: Answer; busy: boolean; onChange: (c: Plan["chart"]) => void }) {
  const current = plan.chart === "AUTO" ? (answer.group_kind === "time" ? "LINE" : "BAR") : plan.chart === "PIE" || plan.chart === "FUNNEL" ? "BAR" : plan.chart;
  const opts: { v: Plan["chart"]; label: string; icon: React.ReactNode }[] = [
    { v: "BAR", label: "Bars", icon: <BarChart3 className="size-3.5" /> },
    ...(answer.group_kind === "time" ? [{ v: "LINE" as const, label: "Line", icon: <LineChart className="size-3.5" /> }] : []),
    { v: "TABLE", label: "Table only", icon: <Table2 className="size-3.5" /> },
  ];
  return (
    <div role="group" aria-label="Chart" className="ml-auto flex overflow-hidden rounded-md border border-border">
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          disabled={busy}
          aria-pressed={current === o.v}
          title={o.label}
          onClick={() => onChange(o.v)}
          className={cn("flex h-8 items-center gap-1 px-2.5 text-xs", current === o.v ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-muted")}
        >
          {o.icon}
          <span className="sr-only sm:not-sr-only">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

/** Builds one condition: a field, how to compare, and the value(s) */
function FilterDialog({ ent, catalog, initial, onClose, onSave }: { ent: EntityDef; catalog: Catalog; initial?: Filter; onClose: () => void; onSave: (f: Filter) => void }) {
  const [key, setKey] = useState(initial?.field?.toLowerCase() || ent.fields[0]?.key || "");
  const field = ent.fields.find((f) => f.key === key);
  const [op, setOp] = useState<Op>(initial?.op || field?.ops[0] || "IS");
  const [values, setValues] = useState<string[]>(initial?.values || []);
  const ops = field?.ops || [];
  const opNow = ops.includes(op) ? op : ops[0];
  const need = field && opNow ? valueCount(opNow, field.type) : 0;
  const ready = !!field && !!opNow && (need === 0 || (need === 2 ? values.filter(Boolean).length >= 2 : values.filter(Boolean).length >= 1));
  const choose = (k: string) => {
    setKey(k);
    setValues([]);
    const f = ent.fields.find((x) => x.key === k);
    if (f && !f.ops.includes(op) && f.ops[0]) setOp(f.ops[0]);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (ready) onSave({ field: key, op: opNow!, values: need === 0 ? [] : values.filter(Boolean) });
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>{initial ? "Change the condition" : "Add a filter"}</DialogTitle>
            <DialogDescription>Only {ent.noun[1]} that match are kept.</DialogDescription>
          </DialogHeader>
          <label className="block space-y-1.5 text-sm font-medium">
            <span>Field</span>
            <select value={key} onChange={(e) => choose(e.target.value)} className={SELECT} aria-label="Field">
              {ent.fields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </label>
          {field && (
            <label className="block space-y-1.5 text-sm font-medium">
              <span>Condition</span>
              <select value={opNow} onChange={(e) => setOp(e.target.value as Op)} className={SELECT} aria-label="Condition">
                {ops.map((o) => (
                  <option key={o} value={o}>
                    {OP_LABEL[o]}
                  </option>
                ))}
              </select>
            </label>
          )}
          {field && opNow && need !== 0 && <ValueInput field={field} op={opNow} catalog={catalog} values={values} onChange={setValues} />}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!ready}>
              {initial ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ValueInput({ field, op, catalog, values, onChange }: { field: FieldDef; op: Op; catalog: Catalog; values: string[]; onChange: (v: string[]) => void }) {
  const need = valueCount(op, field.type);
  const list = useMemo(
    () => (field.type === "person" ? [{ value: "ME", label: "Me" }, ...catalog.people.filter((x) => x.id !== catalog.me).map((x) => ({ value: x.id, label: x.name }))] : field.options || []),
    [field, catalog],
  );
  // A list to pick from (choices and people)
  if (need === "many" && list.length) {
    return (
      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium">Any of</legend>
        <div className="max-h-56 space-y-0.5 overflow-y-auto rounded-md border border-border p-1.5">
          {list.map((o) => {
            const on = values.includes(o.value);
            return (
              <label key={o.value} className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-sm hover:bg-muted">
                <input type="checkbox" checked={on} onChange={() => onChange(on ? values.filter((v) => v !== o.value) : [...values, o.value])} className="size-4 accent-primary" />
                {o.label}
              </label>
            );
          })}
        </div>
      </fieldset>
    );
  }
  const days = op === "IN_LAST_DAYS" || op === "IN_NEXT_DAYS" || op === "OLDER_THAN_DAYS";
  const kind = days || field.type === "number" || field.type === "money" ? "number" : field.type === "date" ? "date" : "text";
  if (need === "many") {
    return (
      <label className="block space-y-1.5 text-sm font-medium">
        <span>Value (separate several with commas)</span>
        <Input value={values.join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((v) => v.trim()))} autoFocus />
      </label>
    );
  }
  return (
    <div className={cn("grid gap-2", need === 2 && "grid-cols-2")}>
      {Array.from({ length: need === 2 ? 2 : 1 }, (_, i) => (
        <label key={i} className="block space-y-1.5 text-sm font-medium">
          <span>{need === 2 ? (i === 0 ? "From" : "To") : days ? "Days" : field.type === "money" ? "Amount (₹)" : "Value"}</span>
          <Input
            type={kind}
            min={kind === "number" && days ? 1 : undefined}
            value={values[i] || ""}
            onChange={(e) => {
              const next = [...values];
              next[i] = e.target.value;
              onChange(next);
            }}
            autoFocus={i === 0}
          />
        </label>
      ))}
    </div>
  );
}

/** "Has / has no" another record, at any time or in the last N days */
function LinkDialog({ ent, onClose, onSave }: { ent: EntityDef; onClose: () => void; onSave: (l: LinkCheck) => void }) {
  const [link, setLink] = useState(ent.links[0]?.key || "");
  const [has, setHas] = useState(false);
  const [days, setDays] = useState("");
  const dated = ent.links.find((l) => l.key === link)?.dated;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const n = Math.round(Number(days));
            onSave({ link, has, within_days: dated && n > 0 ? n : null });
          }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Has / has no</DialogTitle>
            <DialogDescription>Keep {ent.noun[1]} that have, or don't have, another record.</DialogDescription>
          </DialogHeader>
          <div role="radiogroup" aria-label="Has or has no" className="flex overflow-hidden rounded-md border border-border">
            {[
              { v: false, label: "Has no" },
              { v: true, label: "Has a" },
            ].map((o) => (
              <button key={String(o.v)} type="button" role="radio" aria-checked={has === o.v} onClick={() => setHas(o.v)} className={cn("flex-1 py-1.5 text-sm", has === o.v ? "bg-secondary font-medium text-secondary-foreground" : "text-muted-foreground hover:bg-muted")}>
                {o.label}
              </button>
            ))}
          </div>
          <select value={link} onChange={(e) => setLink(e.target.value)} className={SELECT} aria-label="Record">
            {ent.links.map((l) => (
              <option key={l.key} value={l.key}>
                {l.label}
              </option>
            ))}
          </select>
          {dated && (
            <label className="block space-y-1.5 text-sm font-medium">
              <span>In the last … days (empty for any time)</span>
              <Input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} placeholder="Any time" />
            </label>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!link}>
              Add
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
